import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useFeature, PRINT_ORDERING, SCAN_EDITING } from '../contexts/ConfigContext';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './OrderListPage.module.css';
import { queryKeys } from '../api/queryKeys';
import { api } from '../api/client';
import type { OrderPhase, OrderStatus } from '../types/roll';
import { negativesLine, PHASE_LABELS } from '../utils/orderTracking';

/** Where a customer with no orders goes to send film in. */
const STORE_URL = 'https://chemicaldependency.co.uk';

interface Order {
  order_id: string;
  order_number: string;
  order_date: string;
  scan_count: number;
  scan_ready: boolean;
  expired: boolean;
  expired_at?: string;
  /** Where the order is; only for customers with order tracking. */
  status?: OrderStatus;
}

interface OrdersResponse {
  orders: Order[];
  shopify_error: string | null;
}

function fetchOrders(): Promise<OrdersResponse> {
  return api.get<OrdersResponse>('/api/v1/orders');
}

/** The order's status, as its label and the colour family it's shown in. */
function orderLabel(order: Order): { text: string; tone: 'ready' | 'expired' | 'pending' } {
  const phase: OrderPhase | undefined = order.status?.phase;
  if (order.expired || phase === 'expired') return { text: 'Expired', tone: 'expired' };
  if (phase) return { text: PHASE_LABELS[phase], tone: phase === 'ready' ? 'ready' : 'pending' };
  return order.scan_ready ? { text: 'Ready', tone: 'ready' } : { text: 'Pending', tone: 'pending' };
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function OrderListPage() {
  usePageTitle('Your orders');
  const printEnabled = useFeature(PRINT_ORDERING);
  const editingEnabled = useFeature(SCAN_EDITING);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery<OrdersResponse>({
    queryKey: queryKeys.orders(),
    queryFn: fetchOrders,
  });

  const handleRetry = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
  };

  if (isLoading) {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Your Orders</h1>
        <p className={styles.loading}>Loading orders...</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Your Orders</h1>
        <div className={styles.fetchError} role="alert">
          <p>{error instanceof Error ? error.message : 'Something went wrong.'}</p>
          <button className={styles.retryButton} onClick={handleRetry} style={{ marginTop: '1rem' }}>
            Retry
          </button>
        </div>
      </main>
    );
  }

  const orders = data?.orders ?? [];
  const shopifyError = data?.shopify_error ?? null;
  const anyScansReady = orders.some((o) => o.scan_ready);

  return (
    <main className={styles.page}>
      <h1 className={styles.heading}>Your Orders</h1>
      {orders.length > 0 && (
        <p className={styles.intro}>
          {printEnabled
            ? "All your orders are listed below. We'll mark an order Ready as soon as its scans are developed. Then you can view them, download them or order prints."
            : "All your orders are listed below. We'll mark an order Ready as soon as its scans are developed and ready to view."}
        </p>
      )}

      {shopifyError && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorBannerText}>
            Orders could not be refreshed. Showing cached data.
          </span>
          <button className={styles.retryButton} onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}

      {/* Two genuinely different states. Orders in progress mean scans are
          coming; no orders at all means nothing is on its way, and saying
          "not ready yet" there implies a job that does not exist. */}
      {orders.length > 0 && !anyScansReady && (
        <p className={styles.emptyState}>
          None of your orders have scans ready yet. We'll email you as soon as
          they're developed.
        </p>
      )}

      {/* Anyone with a Shopify account can sign in, so this is often someone's
          first look at Loupe. The edit and print lines follow the same flags
          as the features, so nothing is promised that this customer can't use.
          A demo roll will sit here too (roadmap phase 4). */}
      {orders.length === 0 && (
        <section className={styles.welcome} aria-label="Welcome to Loupe">
          <p>
            Welcome to Loupe, from here you can view your development and scan
            orders from Chemical Dependency Lab.
          </p>
          <p>
            {editingEnabled
              ? 'You can make basic edits to your scans and download your scans in a range of sizes.'
              : 'You can download your scans in a range of sizes.'}
          </p>
          {printEnabled && <p>Why not order prints and commit your scans to paper...forever...</p>}
          <p className={styles.emptyState}>
            No orders yet. Once you send film in to the lab, your order will
            appear here.{' '}
            <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
              Visit the store
            </a>
            .
          </p>
        </section>
      )}

      {orders.length > 0 && (
        <ul className={styles.orderList}>
          {orders.map((order) => {
            const label = orderLabel(order);
            const negatives = order.expired ? null : negativesLine(order.status?.negatives);
            // A tracked order still in the lab says where it is instead of "0 scans".
            const showCount = order.scan_count > 0 || !order.status;
            return (
              <li key={order.order_id}>
                <Link to={`/orders/${order.order_id}`} className={styles.orderCard}>
                  <span
                    className={`${styles.statusDot} ${
                      label.tone === 'expired'
                        ? styles.statusDotExpired
                        : label.tone === 'ready'
                          ? styles.statusDotReady
                          : styles.statusDotPending
                    }`}
                    aria-hidden="true"
                  />
                  <div className={styles.orderDetails}>
                    <span className={styles.orderNumber}>{order.order_number}</span>
                    <div className={styles.orderMeta}>
                      <span>{formatDate(order.order_date)}</span>
                      {showCount && (
                        <span>
                          {order.scan_count} {order.scan_count === 1 ? 'scan' : 'scans'}
                        </span>
                      )}
                      <span
                        className={`${styles.statusLabel} ${
                          label.tone === 'expired'
                            ? styles.statusLabelExpired
                            : label.tone === 'ready'
                              ? styles.statusLabelReady
                              : styles.statusLabelPending
                        }`}
                      >
                        {label.text}
                      </span>
                      {order.expired && order.expired_at && (
                        <span className={styles.expiredDate}>Expired {formatDate(order.expired_at)}</span>
                      )}
                    </div>
                    {negatives && <span className={styles.negatives}>{negatives}</span>}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
